import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import type { RangeDateTypeHr } from 'src/types/date.type';
import type { FlatItem, TicketResponses } from 'src/types/elements.type';
import { ApprovalStatus } from 'src/types/helpdesk/ticket.type';
import type {
  ICompensatoryLeaveRemaining,
  ILeaveRemainingYear,
  ILeaveTicket,
  ILeaveTicketAttachment,
  ILeaveTicketDetail,
  LeaveTicketAdd,
  LeaveTicketExport,
  LeaveTicketReportForLineChart,
  LeaveTicketReportForPieChart,
  LeaveTicketReportTakeEmployee
} from 'src/types/hr_ticket/leaveTicket.type';
import type { PagedList } from 'src/types/pagedList.type';
import type { IHrTicketParams } from 'src/types/params.type';
import { http, httpV2 } from 'src/utils/http';

const controller = 'hr/leave-tickets';

export const getLeaveTickets = async (params: IHrTicketParams) => {
  return await http.get<PagedList<ILeaveTicket>>(controller, {
    params: params
  });
};

export const getLeaveTicketsSub = async (params: IHrTicketParams) => {
  return await http.get<PagedList<ILeaveTicket>>(`${controller}/get-by-employee-code`, {
    params: params
  });
};

export const getLeaveTicketDetailsUsed = async (fromDate: string, toDate: string, employeeCode?: string) => {
  return await http.get<ILeaveTicketDetail[]>(`${controller}/used-leaves-detail/${fromDate}/${toDate}`, {
    params: {
      employeeCode
    }
  });
};

export const getLeaveTicketsApproval = async (params: IHrTicketParams) => {
  return await http.get<PagedList<ILeaveTicket>>(`${controller}/waiting-approval`, {
    params: params
  });
};

export const getLeaveTicketsByPackage = async (packageId: number, params: IHrTicketParams) => {
  return await http.get<PagedList<ILeaveTicket>>(`${controller}/package/${packageId}`, {
    params: params
  });
};

export const getLeaveTicket = async (id: number) => {
  return await http.get<ILeaveTicket>(`${controller}/${id}`);
};

export const getLeaveTicketsDropdown = async () => {
  return await http.get<FlatItem[]>(`${controller}/dropdown`);
};

export const getRefLeaveTicketsForTicket = async (refTicketId: string) =>
  await http.get<ILeaveTicket[]>(`${controller}/get-ref-tickets/${refTicketId}`);

export const getLeaveTicketsReportForPieChart = async (
  year: number,
  departmentId?: number,
  month?: number,
  laborType?: number
) => {
  return await http.get<LeaveTicketReportForPieChart[]>(`${controller}/statistics-by-leave-type/${year}`, {
    params: {
      departmentId,
      month,
      laborType
    }
  });
};

export const getLeaveTicketsReportForLineChart = async (
  year: number,
  departmentId?: number,
  laborType?: number,
  employeeId?: string
) => {
  return await http.get<LeaveTicketReportForLineChart[]>(`${controller}/statistics-by-year/${year}`, {
    params: {
      departmentId,
      laborType,
      employeeId
    }
  });
};

export const getLeaveTicketsReportTopEmployee = async (
  year: number,
  take: number,
  departmentId?: number,
  month?: number,
  laborTypeId?: number
) => {
  return await http.get<LeaveTicketReportTakeEmployee[]>(`${controller}/statistics-by-employee/${year}/${take}`, {
    params: {
      departmentId,
      month,
      laborTypeId
    }
  });
};

export const getLeaveTicketDetailsReport = async (
  year: number,
  departmentId?: number,
  month?: number,
  laborTypeId?: number,
  page?: number,
  pageSize?: number,
  orderBy?: string
) => {
  return await http.get<PagedList<ILeaveTicketDetail>>(`${controller}/statistics-by-employee-detail/${year}`, {
    params: {
      departmentId,
      page,
      pageSize,
      orderBy,
      month,
      laborTypeId
    }
  });
};

export const getAllLeaveTicketDetailsReport = async (
  year: number,
  departmentId?: number,
  month?: number,
  laborTypeId?: number
) => {
  return await http.get<ILeaveTicketDetail[]>(`${controller}/statistics-by-employee-detail-all/${year}`, {
    params: {
      departmentId,
      month,
      laborTypeId
    }
  });
};

export const checkLeaveTicketUser = async () => {
  return await http.get<boolean>(`${controller}/check-leave-user`);
};

export const addLeaveTicket = async (leaveTicket: LeaveTicketAdd, isSupport?: boolean) => {
  return await http.post(
    controller,
    leaveTicket,
    // {
    //   ...leaveTicket,
    //   leaveTicketDetails: leaveTicket.leaveTicketDetails.map((item) => ({
    //     ...item,
    //     date: localToUTC(item.date as Date)
    //   }))
    // },
    { params: { isSupport: isSupport ?? false } }
  );
};

export const addMultipleLeaveTicket = async (leaveTickets: LeaveTicketAdd[], isSupport?: boolean) => {
  return await http.post(`${controller}/multiple`, leaveTickets, {
    params: { isSupport: isSupport ?? false }
  });
};

export const updateLeaveTicket = async (id: number, leaveTicket: LeaveTicketAdd) => {
  return await http.put(`${controller}/${id}`, leaveTicket);
};

export const deleteLeaveTicket = async (id: number) => {
  return await http.delete(`${controller}/${id}`);
};

export const cancelLeaveTicket = async (id: number) => {
  return await http.put(`${controller}/${id}/cancel`);
};

export const approvalLeaveTickets = async (
  ticketResponses: TicketResponses[],
  approvalStatus: ApprovalStatus,
  note: string,
  replacerId?: number
) =>
  await http.put(`${controller}/approvals`, {
    ticketResponses: ticketResponses,
    approvalStatus,
    note: note ? note : '',
    replacerId: replacerId
  });

export const cancelApprovalLeaveTicket = async (id: string, step: number) =>
  await http.put(`${controller}/${id}/cancel-approval`, { step });

export const getLeaveRemaining = async (employeeId?: number) => {
  return await http.get<number>(`${controller}/get-leave-remaining`, { params: { employeeId: employeeId } });
};

export const getCompensatoryLeaveRemaining = async (employeeId?: number) => {
  return await http.get<ICompensatoryLeaveRemaining[]>(`${controller}/get-compensatory-leave-remaining`, {
    params: { employeeId }
  });
};

export const printLeaveTicket = async (id: number) =>
  await http.get(`${controller}/print`, {
    params: {
      id: id
    },
    responseType: 'blob'
  });

export const uploadLeaveTicketFile = async (formData: FormData, ticketId: string) => {
  return await http.post<ILeaveTicketAttachment[]>(`${controller}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    params: { ticketId: ticketId }
  });
};

export const getLeaveTicketAttach = async (fileUrl: string, fileName: string) => {
  return http.get<Blob>(`${controller}/get-file`, {
    params: { fileUrl, fileName },
    responseType: 'blob'
  });
};
export const deleteLeaveTicketAttach = async (id: number) => {
  return http.delete(`${controller}/delete-attachments`, {
    params: { leaveTicketAttachId: id }
  });
};

export const getLeaveTicketsHrConfirm = async (
  fromDate: Date,
  toDate: Date,
  page?: number,
  pageSize?: number,
  orderBy?: string,
  departmentId?: number | undefined,
  searchCondition?: string | undefined,
  confirmType?: ConfirmTypeEn | undefined,
  leaveTypeId?: number | undefined,
  sequenceApprovalId?: string | undefined
) => {
  const newFromDate = fromDate.toISOString();
  const newToDate = toDate.toISOString();
  return await httpV2.get<PagedList<ILeaveTicket>>(`${controller}/hr-confirm/${newFromDate}/${newToDate}`, {
    params: {
      page,
      pageSize,
      orderBy,
      departmentId,
      searchCondition,
      confirmType,
      leaveTypeId,
      sequenceApprovalId
    }
  });
};

export const getLeaveTicketsConfirmReport = async (
  fromDate?: Date,
  toDate?: Date,
  departmentId?: number | undefined,
  searchCondition?: string | undefined,
  confirmType?: ConfirmTypeEn | undefined,
  leaveTypeId?: number | undefined,
  sequenceApprovalId?: string | undefined
) => {
  return await httpV2.get<LeaveTicketExport[]>(`${controller}/hr-report`, {
    params: {
      fromDate,
      toDate,
      departmentId,
      searchCondition,
      confirmType,
      leaveTypeId,
      sequenceApprovalId
    }
  });
};

export const hrConfirmLeaveTicket = async (
  id: number | undefined,
  confirmType: ConfirmTypeEn,
  unconfirmedDetails: number[],
  confirmNote?: string
) => await httpV2.put(`${controller}/hr-confirm`, { id, confirmType, unconfirmedDetails, confirmNote });

export const getMyLeaveTicketCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-my-tickets`, {
    params: params
  });
};

export const getLeaveTicketApprovalCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-handle-tickets`, {
    params: params
  });
};

export const getLeaveTicketWaitingProcessingCount = async (params: RangeDateTypeHr) => {
  return await http.get(`${controller}/count-ticket-waiting-processing`, {
    params: params
  });
};

export const getLeaveTicketHrConfirmCount = async (params: RangeDateTypeHr) => {
  return await httpV2.get(`${controller}/count-hr-confirm-tickets`, {
    params: params
  });
};

export const getLeaveTicketsHrConfirmByCondition = async (confirmType: ConfirmTypeEn, employeeCode: string) => {
  return await httpV2.get<ILeaveTicket[]>(`${controller}/get-list-by-condition`, {
    params: {
      confirmType,
      employeeCode
    }
  });
};

export const transferRemainingLeaves = async (year: number) =>
  await http.put(`${controller}/transfer-remaining-leave/${year}`, { year }, { timeout: 60 * 1000 * 2 });

export const getLeaveRemainingYear = async (
  year: number,
  departmentId?: number,
  page?: number,
  pageSize?: number,
  orderBy?: string
) => {
  return await http.get<PagedList<ILeaveRemainingYear>>(`${controller}/remaining-leaves/${year}`, {
    params: {
      departmentId,
      page,
      pageSize,
      orderBy
    }
  });
};

export const approvalQuickLeaveTickets = async (ticketIds: number[], approvalStatus: ApprovalStatus, note?: string) => {
  return await httpV2.put(`${controller}/approvals`, ticketIds, {
    params: { approvalStatus, note }
  });
};
